import React, { useEffect, useState, useRef, useCallback } from "react";
import "./ChatInput.css";
import ChatSelector from "./ChatSelector";
import ThemeSwitcher from "./ThemeSwitcher";
import { useMessageStore } from "../store/store";


const directionMap = {
    'ArrowUp': 'north',
    'KeyW': 'north',
    'ArrowDown': 'south',
    'KeyS': 'south',
    'ArrowRight': 'east',
    'KeyD': 'east',
    'ArrowLeft': 'west',
    'KeyA': 'west',
    'PageUp': 'up',
    'KeyR': 'up',
    'PageDown': 'down',
    'KeyF': 'down'
};

function ChatInput({ sendMessage, currentTheme, onThemeChange }) {
    const [input, setInput] = useState('');
    const [moving, setMoving] = useState(false);
    const [shiftDown, setShiftDown] = useState(false);
    const [chatHistory, setChatHistory] = useState([]);
    const [historyPosition, setHistoryPosition] = useState(0);
    const [channel, setChannel] = useState(0);
    const [placeholder, setPlaceholder] = useState("Type a message or command... (Press 'C' to focus)");

    const inputRef = useRef(null);

    // Update placeholder based on selected channel
    useEffect(() => {
        switch (channel) {
            case 0:
                setPlaceholder("Say: Type a message... (Press 'C' to focus)");
                break;
            case 1:
                setPlaceholder("Global: Type a message... (Press 'C' to focus)");
                break;
            case 2:
                setPlaceholder("Party: Type a message... (Press 'C' to focus)");
                break;
            case 3:
                setPlaceholder("Clan: Type a message... (Press 'C' to focus)");
                break;
            default:
                setPlaceholder("Type a message or command... (Press 'C' to focus)");
        }
    }, [channel]);

    function hasFocus() {
        return document.activeElement === inputRef.current;
    }
    function setFocus() {
        inputRef.current.focus();
    }

    function removeFocus() {
        inputRef.current.blur();
    }

    const handleKeyPress = useCallback((event) => {
        const { code, shiftKey, ctrlKey, metaKey } = event;

        if (hasFocus()) {
            if (code === 'Escape') {
                removeFocus();
            }
        } else if (code === 'KeyC') {
            setFocus();
            event.preventDefault();
        } else if (code === 'Slash') {
            setFocus();
        } else {
            const direction = directionMap[code];
            // Only process movement if shift and ctrl/cmd are not held down
            // (to avoid conflicts with chat history, hotkey switching, and keyboard shortcuts)
            if (direction && !shiftKey && !ctrlKey && !metaKey) {
                startMoving(direction);
            }
        }
    }, [moving]);

    const handleKeyRelease = useCallback((event) => {
        if (hasFocus()) {
            return;
        }
        if (directionMap[event.code]) {
            stopMoving();
        }
    }, [moving]);

    useEffect(() => {
        window.addEventListener('keydown', handleKeyPress);
        window.addEventListener('keyup', handleKeyRelease)

        return () => {
            window.removeEventListener('keydown', handleKeyPress);
            window.removeEventListener('keyup', handleKeyRelease);
        };
    }, [handleKeyPress, handleKeyRelease]);

    const handleKeyDown = (event) => {
        if (shiftDown) {
            handleHistory(event);
            return;
        }

        if (!moving && event.key === 'Shift') {
            setShiftDown(true);
            return;
        }

        // Don't process movement if Ctrl/Cmd is held (for keyboard shortcuts)
        if (event.ctrlKey || event.metaKey) {
            return;
        }

        if (!moving && input === '') {
            switch (event.key) {
                case 'ArrowUp':
                    startMoving('north');
                    break;
                case 'ArrowDown':
                    startMoving('south');
                    break;
                case 'ArrowLeft':
                    startMoving('west');
                    break;
                case 'ArrowRight':
                    startMoving('east');
                    break;
                case 'PageUp':
                    startMoving('up');
                    break;
                case 'PageDown':
                    startMoving('down');
                    break;
                default:
                    break;
            }
        }
    }

    const handleKeyUp = (event) => {
        if (event.key === 'Shift') {
            setHistoryPosition(0);
            setShiftDown(false);
        }

        switch (event.key) {
            case 'ArrowUp':
            case 'ArrowDown':
            case 'ArrowLeft':
            case 'ArrowRight':
            case 'PageUp':
            case 'PageDown':
                stopMoving();
                break;
            default:
                break;
        }

    }

    const handleHistory = (event) => {
        if (historyPosition < chatHistory.length) {
            if (event.key === 'ArrowUp') {
                const pos = historyPosition + 1;
                setInput(chatHistory[chatHistory.length - pos]);
                setHistoryPosition(pos);
            }
            if (event.key === 'ArrowDown' && historyPosition > 0) {
                const pos = historyPosition - 1;
                setInput(chatHistory[chatHistory.length - historyPosition]);
                setHistoryPosition(pos);
            }
        }
    }

    const handleSubmit = (event) => {
        event.preventDefault();
        const msg = input.trim();
        if (msg.length > 0) {
            // Check for clear commands
            const clearMatch = msg.match(/^\/(clear|cls)(?:\s+(\d))?$/i);
            if (clearMatch) {
                const { clearMessages, clearMessages2, clearAllMessages } = useMessageStore.getState();
                const consoleNum = clearMatch[2];
                if (consoleNum === '1') {
                    clearMessages();
                } else if (consoleNum === '2') {
                    clearMessages2();
                } else {
                    clearAllMessages();
                }
                setInput('');
                return;
            }
            
            if(msg.startsWith('/')){
                sendMessage('600' + msg);
            } else {
                switch (channel) {
                    case 0:
                        sendMessage('600' + msg);
                        break;
                    case 1:
                        sendMessage('600/g ' + msg);
                        break;
                    case 2:
                        sendMessage('600/p ' + msg);
                        break
                    case 3:
                        break;
                }
            }
            if (chatHistory[chatHistory.length - 1] !== msg) {
                setChatHistory(prev => [...prev, msg]);
            }
            setInput('');
        }
    }

    function startMoving(direction) {
        if (!moving) {
            setMoving(true);
            move(direction);
        }
    }

    function stopMoving() {
        move('stop');
        setMoving(false);
    }

    const move = (direction) => {
        sendMessage('310' + direction);
    }

    return (
        <>
            <form className="input-form" onSubmit={handleSubmit}>
                <ChatSelector setChannel={setChannel} />
                <input
                    ref={inputRef}
                    className="chat-input"
                    type="text"
                    onChange={(e) => setInput(e.target.value)}
                    value={input}
                    onKeyDown={handleKeyDown}
                    onKeyUp={handleKeyUp}
                    placeholder={placeholder}
                    aria-label="Chat input"
                />
                {currentTheme && onThemeChange && (
                    <ThemeSwitcher currentTheme={currentTheme} onThemeChange={onThemeChange} />
                )}
            </form>
        </>
    )
}

export default ChatInput;